import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import Stepper from '../components/stepper/Stepper';
import { toast } from 'react-hot-toast';

const defaultCertificate = {
  certificate_name: '',
  certificate_completion_id: '',
  certification_url: '',
  start_date: '',
  end_date: '',
  certificate_file: null,
  certificate_preview: null,
  responsibilities: ''
};

const isFormComplete = (skills, certificateList) => {
  // Check if any certificate has partial data
  const hasPartialCertificate = certificateList.some(cert => {
    const hasAnyField = cert.certificate_name || 
      cert.certificate_completion_id || 
      cert.certification_url || 
      cert.start_date || 
      cert.end_date || 
      cert.certificate_file || 
      cert.responsibilities;

    const hasAllRequiredFields = cert.certificate_name &&
      cert.certificate_completion_id &&
      cert.start_date &&
      cert.end_date;

    // Return true if there's partial data (some fields filled but not all required ones)
    return hasAnyField && !hasAllRequiredFields;
  });

  return !hasPartialCertificate;
};

const isFormEmpty = (skills, certificateList) => {
  // Check if there are any skills
  const hasSkills = skills.length > 0;

  // Check if any certificate has any data
  const hasCertificateData = certificateList.some(cert => 
    cert.certificate_name || 
    cert.certificate_completion_id || 
    cert.certification_url || 
    cert.start_date || 
    cert.end_date || 
    cert.certificate_file || 
    cert.responsibilities
  );

  return !hasSkills && !hasCertificateData;
};

function SkillsCertifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [certificateList, setCertificateList] = useState([{ ...defaultCertificate }]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSkillsAndCertifications();
    }
  }, [user]);

  const fetchSkillsAndCertifications = async () => {
    try {
      const { data, error } = await supabase
        .from('profile_skill_certifications')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      if (data && data.length > 0) {
        setSkills(data[0].skills || []);
        if (data[0].certificate_name) {
          setCertificateList(data.map(cert => ({
            certificate_name: cert.certificate_name,
            certificate_completion_id: cert.certificate_completion_id,
            certification_url: cert.certification_url,
            start_date: cert.start_date,
            end_date: cert.end_date,
            certificate_file: null,
            certificate_preview: null,
            responsibilities: cert.responsibilities
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching skills and certifications:', error);
      toast.error('Failed to load skills and certifications');
    }
  };

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (newSkill.trim()) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setSkills(skills.filter(skill => skill !== skillToRemove));
  };

  const handleInputChange = (index, field, value) => {
    const newCertificateList = [...certificateList];
    newCertificateList[index] = {
      ...newCertificateList[index],
      [field]: value
    };
    setCertificateList(newCertificateList);
  };

  const handleFileChange = (index, file) => {
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size should not exceed 2MB');
        return;
      }
      
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/rtf',
        'image/jpeg',
        'image/png',
        'image/gif'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, DOC, DOCX, RTF, JPG, PNG, and GIF files are allowed');
        return;
      }

      const newCertificateList = [...certificateList];
      
      const preview = file.type.startsWith('image/') 
        ? URL.createObjectURL(file)
        : file.type === 'application/pdf'
        ? '/pdf-icon.png'
        : '/doc-icon.png';

      newCertificateList[index] = {
        ...newCertificateList[index],
        certificate_file: file,
        certificate_preview: preview
      };
      
      setCertificateList(newCertificateList);
    }
  };

  const handleDrop = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(index, file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addMoreCertificate = () => {
    setCertificateList([...certificateList, { ...defaultCertificate }]);
  };

  const removeCertificate = (index) => {
    if (certificateList.length > 1) {
      const newCertificateList = certificateList.filter((_, i) => i !== index);
      setCertificateList(newCertificateList);
    }
  };

  const uploadCertificate = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Math.random().toString(36).slice(2)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('certificates')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    return filePath;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if form is completely empty
    if (isFormEmpty(skills, certificateList)) {
      // If form is empty, proceed directly
      navigate('/dashboard');
      return;
    }

    // Check if form has partial data
    if (!isFormComplete(skills, certificateList)) {
      // Show warning if form has partial data
      if (!window.confirm('You have started filling the form. Are you sure you want to proceed without completing it?')) {
        return;
      }
    }

    setIsUploading(true);

    try {
      // Delete existing records
      await supabase
        .from('profile_skill_certifications')
        .delete()
        .eq('user_id', user.id);

      // Save the data
      const certificatesWithFiles = await Promise.all(
        certificateList.map(async (cert) => {
          let filePath = null;
          if (cert.certificate_file) {
            filePath = await uploadCertificate(cert.certificate_file);
          }
          return {
            user_id: user.id,
            skills,
            certificate_name: cert.certificate_name,
            certificate_completion_id: cert.certificate_completion_id,
            certification_url: cert.certification_url,
            start_date: cert.start_date,
            end_date: cert.end_date,
            certificate_file_path: filePath,
            responsibilities: cert.responsibilities
          };
        })
      );

      const { error } = await supabase
        .from('profile_skill_certifications')
        .insert(certificatesWithFiles);

      if (error) throw error;

      toast.success('Skills and certifications saved successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving skills and certifications:', error);
      toast.error('Failed to save data. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-primary-50">
      <Navigation />
      <div className="flex-grow pt-16 md:pt-20 mt-2 md:mt-5 mb-4">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Mobile Stepper */}
          <div className="block md:hidden mb-6">
            <Stepper />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
            {/* Desktop Stepper */}
            <div className="hidden md:block md:col-span-3">
              <div className="sticky top-24">
                <Stepper />
              </div>
            </div>

            {/* Form Section */}
            <div className="col-span-1 md:col-span-9">
              <div className="bg-primary-extra rounded-lg shadow-sm p-4 md:p-8">
                <div className="flex justify-between items-center mb-6 md:mb-8">
                  <h1 className="text-xl md:text-2xl font-bold text-primary-500">Skills & Certifications</h1>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                  {/* Skills Section */}
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-primary-text">Add Your Skills (Optional)</h2>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {skills.map((skill, index) => (
                        <span
                          key={index}
                          className="bg-primary-100 px-3 py-1 rounded-full flex items-center gap-2 text-sm"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => handleRemoveSkill(skill)}
                            className="text-primary-text hover:text-red-500"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        placeholder="Add Skills"
                        className="flex-1 rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 text-sm focus:border-primary-400 focus:ring-primary-400"
                      />
                      <button
                        type="button"
                        onClick={handleAddSkill}
                        className="px-4 py-2.5 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90 text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Certificates Section */}
                  <div className="space-y-6">
                    <h2 className="text-lg font-semibold text-primary-text">Add Your Certificate (Optional)</h2>
                    {certificateList.map((certificate, index) => (
                      <div key={index} className="space-y-4 md:space-y-6 pb-6 md:pb-8 border-b border-primary-400">
                        {index > 0 && (
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => removeCertificate(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                          <div>
                            <label className="block text-sm font-medium text-primary-text mb-1">
                              Certificate name
                            </label>
                            <input
                              type="text"
                              value={certificate.certificate_name}
                              onChange={(e) => handleInputChange(index, 'certificate_name', e.target.value)}
                              placeholder="Add Certificate Name"
                              className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary-text mb-1">
                              Certificate completion ID
                            </label>
                            <input
                              type="text"
                              value={certificate.certificate_completion_id}
                              onChange={(e) => handleInputChange(index, 'certificate_completion_id', e.target.value)}
                              placeholder="Please Add"
                              className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-text mb-1">
                            Certification URL
                          </label>
                          <input
                            type="url"
                            value={certificate.certification_url}
                            onChange={(e) => handleInputChange(index, 'certification_url', e.target.value)}
                            placeholder="Please Add"
                            className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                          <div>
                            <label className="block text-sm font-medium text-primary-text mb-1">
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={certificate.start_date}
                              onChange={(e) => handleInputChange(index, 'start_date', e.target.value)}
                              className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary-text mb-1">
                              End Date
                            </label>
                            <input
                              type="date"
                              value={certificate.end_date}
                              onChange={(e) => handleInputChange(index, 'end_date', e.target.value)}
                              className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-text mb-1">
                            Upload Certificate
                          </label>
                          <div
                            onClick={() => document.getElementById(`file-upload-${index}`).click()}
                            onDrop={(e) => handleDrop(index, e)}
                            onDragOver={handleDragOver}
                            className="mt-1 cursor-pointer flex flex-col items-center justify-center px-4 md:px-6 pt-4 md:pt-5 pb-4 md:pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-500 hover:bg-primary-50 transition-colors"
                          >
                            <input
                              id={`file-upload-${index}`}
                              type="file"
                              className="hidden"
                              accept=".pdf,.doc,.docx,.rtf,.jpg,.jpeg,.png,.gif"
                              onChange={(e) => handleFileChange(index, e.target.files[0])}
                            />
                            
                            {certificate.certificate_preview ? (
                              <div className="space-y-2 text-center">
                                {certificate.certificate_file.type.startsWith('image/') ? (
                                  <img
                                    src={certificate.certificate_preview}
                                    alt="Certificate preview"
                                    className="mx-auto h-24 md:h-32 w-auto object-contain"
                                  />
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <svg
                                      className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-400"
                                      stroke="currentColor"
                                      fill="none"
                                      viewBox="0 0 48 48"
                                      aria-hidden="true"
                                    >
                                      <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span className="mt-2 block text-sm font-medium text-gray-900">
                                      {certificate.certificate_file.name}
                                    </span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newCertificateList = [...certificateList];
                                    newCertificateList[index] = {
                                      ...newCertificateList[index],
                                      certificate_file: null,
                                      certificate_preview: null
                                    };
                                    setCertificateList(newCertificateList);
                                  }}
                                  className="text-xs md:text-sm text-red-500 hover:text-red-700"
                                >
                                  Remove file
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-1 text-center">
                                <svg
                                  className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-400"
                                  stroke="currentColor"
                                  fill="none"
                                  viewBox="0 0 48 48"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <div className="flex text-xs md:text-sm text-gray-600 justify-center">
                                  <span className="relative cursor-pointer rounded-md font-medium text-primary-500 hover:text-primary-400">
                                    Click to upload
                                  </span>
                                  <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">
                                  PDF, DOC, DOCX, RTF, JPG, PNG, GIF up to 2MB
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-primary-text mb-1">
                            Responsibilities
                          </label>
                          <textarea
                            value={certificate.responsibilities}
                            onChange={(e) => handleInputChange(index, 'responsibilities', e.target.value)}
                            placeholder="Add here"
                            className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 h-24 md:h-32 text-sm focus:border-primary-400 focus:ring-primary-400"
                          />
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={addMoreCertificate}
                        className="text-primary-500 hover:text-primary-600 font-medium text-sm"
                      >
                        + Add More Certificate
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/work-experience')}
                      className="px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isUploading}
                      className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90 text-sm"
                    >
                      {isUploading ? 'Saving...' : 'Save & Next'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default SkillsCertifications;